import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["title"];
import styled from 'foundations-theming/utils';
import { useCallback } from 'react';
import { useProxiedFetchGeneratedContent, useFetchContentAssistanceContent } from '../hooks/useFetchContentAssistanceContent';
import UIButtonGroup from 'UIComponents/button/UIButtonGroup';
import ContentAssistantLoadingScreen from '../screens/ContentAssistantLoadingScreen';
import PreviewGeneratedContent from '../screens/PreviewGeneratedContent';
import { FINISHED, STARTED, ERROR, REFRESH_ERROR } from '../hooks/constants';
import { RightIcon } from 'foundations-assets';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UIClickable from 'UIComponents/button/UIClickable';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { useInlineToolbarState } from './useInlineToolbarState';
import { useUsageTracker } from '../hooks/useUsageTracker';
import { tooltipBorder } from '../screens/SharedStyleComponents';
import { MAX_RETRIES } from '../screens/constants';
import ContentAssistantError from '../screens/ContentAssistantError';
import BrandVoiceText from '../core/BrandVoiceText';
import { generateFeedbackAnnotatedResults } from '../api/contentAssistanceApi';
import { getBrandVoiceUrl } from '../utils/urls';
import { Link } from 'foundations-components/transitional/components/Link';
import { View } from 'foundations-components/transitional/components/View';

// Text selection menus
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BUTTON_PADDING = '8px 16px';
const CustomButton = styled(UIClickable).withConfig({
  displayName: "InlineToolbar__CustomButton",
  componentId: "bv5nwy-0"
})(["transition:background-color 100ms;padding:", ";&:hover{background-color:", ";}"], BUTTON_PADDING, ({
  theme
}) => theme.color['do-not-use-calypso-light']);
const DisabledButton = styled.div.withConfig({
  displayName: "InlineToolbar__DisabledButton",
  componentId: "bv5nwy-1"
})(["color:", ";cursor:not-allowed;padding:", ";"], ({
  theme
}) => theme.color['do-not-use-battleship'], BUTTON_PADDING);
const DropdownWrapper = styled.div.withConfig({
  displayName: "InlineToolbar__DropdownWrapper",
  componentId: "bv5nwy-2"
})(["padding:", " 0;", ""], ({
  theme
}) => theme.space['200'], tooltipBorder);
const MenuWrapper = styled.div.withConfig({
  displayName: "InlineToolbar__MenuWrapper",
  componentId: "bv5nwy-3"
})(["display:flex;flex-direction:row;"]);

// `This is used so MenuWrapper's click event isn't invoked
const StopPropagation = ({
  children
}) => {
  return /*#__PURE__*/_jsx("span", {
    onClick: e => e.stopPropagation(),
    children: children
  });
};

/**
 * Note: only appears for Content Assistance module text edit (requires gated to Copilot + Copilot Collaborate)
 * @deprecated - use BrandVoiceMessage from ui-brand-identity-lib instead
 */
const getTooltipProps = (className = '', settingsLink, brandVoiceOptions) => {
  const {
    brandKitId,
    isSetUp,
    isEnabledForApp
  } = brandVoiceOptions || {};
  return {
    className,
    title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: isSetUp && !isEnabledForApp ? 'baseContentGenerationModal.inlineToolbar.brandVoiceDisabledForAppTooltip_jsx' : 'baseContentGenerationModal.inlineToolbar.brandVoiceNotSetUpTooltip_jsx',
      elements: {
        UILink: Link
      },
      options: {
        href: getBrandVoiceUrl(brandKitId),
        external: true
      }
    })
  };
};
const MainMenu = ({
  brandVoiceOptions,
  handleOnClick,
  setHoveredCommand,
  selectionCommands,
  settingsLink,
  tooltipClassName
}) => {
  return /*#__PURE__*/_jsx(UIButtonGroup, {
    orientation: "vertical",
    style: {
      minWidth: '140px'
    },
    children: /*#__PURE__*/_jsx(StopPropagation, {
      children: /*#__PURE__*/_jsx(DropdownWrapper, {
        children: selectionCommands.map(command => {
          // TODO: Figure out how to render a submenu here

          const onMouseEnter = () => {
            if (command.subMenu) {
              setHoveredCommand(command);
            } else {
              setHoveredCommand(null);
            }
          };
          if (command.disabled) {
            const _getTooltipProps = getTooltipProps(tooltipClassName, settingsLink, brandVoiceOptions),
              {
                title
              } = _getTooltipProps,
              tooltipProps = _objectWithoutPropertiesLoose(_getTooltipProps, _excluded);
            return /*#__PURE__*/_jsxs(DisabledButton, {
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: command.labelKey
              }), /*#__PURE__*/_jsx(HelpIcon, {
                "data-test-id": "disabled-command-icon",
                title: title,
                tooltipProps: tooltipProps,
                marginInlineStart: "100"
              })]
            }, command.commandId);
          }
          return /*#__PURE__*/_jsxs(CustomButton, {
            onMouseEnter: onMouseEnter,
            onClick:
            // No onclick for buttons with sub menus
            !command.subMenu ? () => {
              handleOnClick(command, command.parameters);
            } : undefined,
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: command.labelKey
            }), command.subMenu && /*#__PURE__*/_jsx(View, {
              as: "span",
              marginInlineStart: "400",
              children: /*#__PURE__*/_jsx(RightIcon, {
                size: "300",
                color: "do-not-use-eerie"
              })
            })]
          }, command.commandId);
        })
      })
    })
  });
};
const SubMenu = ({
  command,
  handleOnClick
}) => {
  if (!command || !command.subMenu) {
    return null;
  }
  return /*#__PURE__*/_jsx(UIButtonGroup, {
    orientation: "vertical",
    style: {
      minWidth: '140px'
    },
    children: /*#__PURE__*/_jsx(StopPropagation, {
      children: /*#__PURE__*/_jsx(DropdownWrapper, {
        children: command.subMenu.map((subMenuOption, index) => {
          return /*#__PURE__*/_jsx(CustomButton, {
            onClick: () => handleOnClick(command, subMenuOption.parameters),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: subMenuOption.labelKey
            })
          }, `${command.commandId}-${index}`);
        })
      })
    })
  });
};
const createToolbarComponent = useFetchContent => {
  return ({
    brandVoiceOptions,
    onClosePopover,
    onTextChange,
    selectionCommands,
    editorContext,
    trackInteraction,
    usageDescription,
    language,
    objectId,
    settingsLink,
    tips,
    tooltipClassName
  }) => {
    const {
      state,
      actions
    } = useInlineToolbarState();
    const {
      onSuccess,
      setGeneratedContent,
      setHoveredCommand,
      setSelectedCommand,
      incrementCount,
      setShowResult,
      setExtraParams
    } = actions;
    const {
      selectedCommand,
      hoveredCommand,
      generatedContent,
      regenerateClickCount,
      selectedResultIndex,
      allResults,
      previousExtraParams
    } = state;
    const onError = useCallback(e => {
      console.error('Something went wrong ', e);
    }, []);
    const {
      fetch,
      request,
      sendFeedback,
      error: __error,
      result: __result
    } = useFetchContent({
      onSuccess,
      onError,
      language,
      objectId
    });
    const trackUsage = useUsageTracker({
      trackInteraction,
      usageDescription
    });
    const handleFetch = useCallback((command, extraParams = {}) => {
      setSelectedCommand(command);
      setExtraParams(extraParams);
      fetch({
        commandId: command.commandId,
        parameters: Object.assign({
          editorContext
        }, extraParams)
      });
    }, [setSelectedCommand, fetch, editorContext, setExtraParams]);
    const {
      isSetUp,
      isEnabledForApp
    } = brandVoiceOptions || {};
    const usingBrandVoice = Boolean(isSetUp && isEnabledForApp);
    const handleOnClick = useCallback((command, extraParams = {}) => {
      const trackingParams = extraParams.toneOfVoice ? {
        choice: extraParams.toneOfVoice
      } : {};
      trackUsage('menu-selection', Object.assign({
        selected: command.commandId,
        usingBrandVoice
      }, trackingParams));
      handleFetch(command, extraParams);
    }, [handleFetch, trackUsage, usingBrandVoice]);
    const handleOnClickTryAgain = useCallback(() => {
      const command = selectedCommand;
      const trackingParams = previousExtraParams.toneOfVoice ? {
        choice: previousExtraParams.toneOfVoice
      } : {};
      trackUsage('click-try-again', Object.assign({
        selected: command.commandId
      }, trackingParams));
      handleFetch(command, previousExtraParams);
    }, [handleFetch, trackUsage, selectedCommand, previousExtraParams]);
    const handleRefresh = useCallback(() => {
      incrementCount();
      handleFetch(selectedCommand, previousExtraParams);
    }, [incrementCount, handleFetch, selectedCommand, previousExtraParams]);
    const handleClosePopover = useCallback(() => {
      onClosePopover();
      const feedbackAnnotatedResults = generateFeedbackAnnotatedResults(generatedContent, allResults, false);
      sendFeedback(feedbackAnnotatedResults);
    }, [allResults, generatedContent, onClosePopover, sendFeedback]);
    const submitCurrentText = useCallback(() => {
      const feedbackAnnotatedResults = generateFeedbackAnnotatedResults(generatedContent, allResults);
      sendFeedback(feedbackAnnotatedResults);
      // Should always(?) be true, but in case it isn't we should have some error fallback
      if (generatedContent && generatedContent.text) {
        onTextChange(generatedContent.text);
      }
    }, [generatedContent, allResults, sendFeedback, onTextChange]);
    let label = selectedCommand ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: selectedCommand.labelKey
    }) : null;
    if (selectedCommand && selectedCommand.beta) {
      label = /*#__PURE__*/_jsxs("div", {
        className: "align-center",
        children: [label, /*#__PURE__*/_jsx(Badge, {
          use: "beta",
          marginInlineStart: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "baseContentGenerationModal.inlineToolbar.betaBadge"
          })
        })]
      });
    }
    const brandVoiceLink = isSetUp && isEnabledForApp && selectedCommand && selectedCommand.brandVoiceLinkKey && /*#__PURE__*/_jsx(BrandVoiceText, {
      brandVoiceLinkKey: selectedCommand.brandVoiceLinkKey,
      brandVoiceOptions: brandVoiceOptions
    });
    if (request === STARTED) {
      return /*#__PURE__*/_jsx(ContentAssistantLoadingScreen, {
        label: label,
        body: brandVoiceLink,
        onCancel: onClosePopover,
        tips: tips
      });
    } else if (request === FINISHED) {
      const trackingProperties = {
        command: selectedCommand.commandId,
        // regenerateClickCount starts at 0 but should be tracked starting from 1
        regenerateClickCount: regenerateClickCount + 1,
        usingBrandVoice
      };
      if (previousExtraParams.toneOfVoice) {
        trackingProperties.choice = previousExtraParams.toneOfVoice;
      }
      if (allResults.length === 0) {
        return /*#__PURE__*/_jsx(ContentAssistantError, {
          label: label,
          body: brandVoiceLink,
          onCancel: onClosePopover,
          onTryAgain: handleOnClickTryAgain
        });
      }
      return /*#__PURE__*/_jsx(PreviewGeneratedContent, {
        content: generatedContent,
        updateContent: setGeneratedContent,
        onSubmit: submitCurrentText,
        onCancel: handleClosePopover,
        onRefresh: handleRefresh,
        isRefreshDisabled: regenerateClickCount >= MAX_RETRIES,
        label: label,
        body: brandVoiceLink,
        setShowResult: setShowResult,
        resultCount: allResults.length,
        trackUsage: trackUsage,
        selectedResultIndex: selectedResultIndex,
        trackingProperties: trackingProperties
      });
    } else if (request === ERROR || request === REFRESH_ERROR) {
      return /*#__PURE__*/_jsx(ContentAssistantError, {
        label: label,
        body: brandVoiceLink,
        onCancel: onClosePopover,
        onTryAgain: handleOnClickTryAgain
      });
    }

    // This is the floating inline toolbar version that shows up when select some text
    return (
      /*#__PURE__*/
      // Close the menu if the container is clicked, but the events inside handleClick prevent propagation, so this won't happen
      // if a button is clicked
      _jsxs(MenuWrapper, {
        onClick: onClosePopover,
        children: [/*#__PURE__*/_jsx(MainMenu, {
          brandVoiceOptions: brandVoiceOptions,
          handleOnClick: handleOnClick,
          selectionCommands: selectionCommands,
          setHoveredCommand: setHoveredCommand,
          settingsLink: settingsLink,
          tooltipClassName: tooltipClassName
        }), hoveredCommand && hoveredCommand.subMenu && /*#__PURE__*/_jsx(SubMenu, {
          command: hoveredCommand,
          handleOnClick: handleOnClick
        })]
      })
    );
  };
};
export const ProxiedInlineToolbar = createToolbarComponent(useProxiedFetchGeneratedContent);
export const InlineToolbar = createToolbarComponent(useFetchContentAssistanceContent);